import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Sparkles, Send, ShieldAlert, Loader2, RotateCcw, Plus, History } from 'lucide-react';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';

const apiCall = async (path, opts = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const STARTER_PROMPTS = [
  'What sources can I lawfully use to find a current employer in Texas?',
  'Walk me through documenting permissible purpose under GLBA for a skip trace.',
  'How should I structure an investigative report for a divorce case?',
  'What is the proper way to baseline a witness before a cognitive interview?',
];

const Bubble = ({ role, text, refused }) => {
  const isUser = role === 'user';
  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
      data-testid={`aic-bubble-${role}`}
    >
      <div
        className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-amber-500/15 border border-amber-400/40 text-amber-50'
            : refused
            ? 'bg-rose-900/30 border border-rose-500/40 text-rose-100'
            : 'bg-[#0E1626] border border-[#1E293B] text-slate-100'
        }`}
      >
        {!isUser && refused && (
          <div className="flex items-center gap-1.5 text-rose-300 text-[11px] font-mono uppercase tracking-wider mb-1.5">
            <ShieldAlert className="w-3.5 h-3.5" /> Refusal · policy enforced
          </div>
        )}
        {!isUser && !refused && (
          <div className="flex items-center gap-1.5 text-amber-300 text-[11px] font-mono uppercase tracking-wider mb-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Coach
          </div>
        )}
        {text}
      </div>
    </div>
  );
};

const AiCoachPanel = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const r = await apiCall('/api/training-center/ai-coach/sessions');
      setSessions(r.sessions || []);
    } catch (e) { /* swallow */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const loadHistory = async (sid) => {
    try {
      const r = await apiCall(`/api/training-center/ai-coach/history?session_id=${encodeURIComponent(sid)}`);
      const flat = [];
      (r.messages || []).forEach(m => {
        if (m.msg) flat.push({ role: 'user', text: m.msg });
        if (m.reply) flat.push({ role: 'coach', text: m.reply, refused: !!m.refused });
      });
      setMessages(flat);
      setSessionId(sid);
      setShowHistory(false);
    } catch (e) { toast.error(e.message); }
  };

  const newConversation = () => {
    setMessages([]);
    setSessionId('');
    setInput('');
  };

  const send = async (overrideText) => {
    const txt = (overrideText ?? input).trim();
    if (!txt || sending) return;
    setSending(true);
    setMessages(m => [...m, { role: 'user', text: txt }]);
    setInput('');
    try {
      const r = await apiCall('/api/training-center/ai-coach/chat', {
        method: 'POST',
        body: JSON.stringify({ message: txt, session_id: sessionId || undefined }),
      });
      if (!sessionId && r.session_id) setSessionId(r.session_id);
      setMessages(m => [...m, { role: 'coach', text: r.reply, refused: !!r.refused }]);
      loadSessions();
    } catch (e) {
      toast.error(e.message);
      setMessages(m => [...m, { role: 'coach', text: `Error: ${e.message}`, refused: false }]);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4" data-testid="ai-coach-panel">
      {/* Chat */}
      <div className="rounded-lg border border-[#1E293B] bg-[#0B1220] overflow-hidden flex flex-col" style={{ minHeight: 560 }}>
        <div className="px-4 py-3 border-b border-[#1E293B] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-300" />
            <h4 className="text-sm font-bold text-white">AI Investigator Coach</h4>
            <span className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.18em]">Claude · refusal policy active</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={newConversation}
              className="text-xs font-mono uppercase tracking-wider text-slate-300 hover:text-amber-300 inline-flex items-center gap-1 border border-slate-700 hover:border-amber-500/50 rounded px-2.5 py-1"
              data-testid="aic-new-btn"
            >
              <Plus className="w-3.5 h-3.5" /> New
            </button>
            <button
              onClick={() => setShowHistory(s => !s)}
              className="lg:hidden text-xs font-mono uppercase tracking-wider text-slate-300 hover:text-amber-300 inline-flex items-center gap-1 border border-slate-700 rounded px-2.5 py-1"
              data-testid="aic-history-toggle"
            >
              <History className="w-3.5 h-3.5" /> History
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4" data-testid="aic-thread">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto">
              <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-400/30 flex items-center justify-center mb-3">
                <Sparkles className="w-6 h-6 text-amber-300" />
              </div>
              <h5 className="text-base font-bold text-white">Ask your investigator coach.</h5>
              <p className="text-xs text-slate-400 mt-1.5">
                Senior-mentor guidance on lawful tradecraft, Texas Occupations Code Ch. 1702, federal privacy law (FCRA / GLBA / DPPA / HIPAA / ECPA), interviewing, and report writing.
              </p>
              <div className="mt-5 w-full grid grid-cols-1 gap-2" data-testid="aic-starters">
                {STARTER_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => send(p)}
                    className="text-left text-xs text-slate-200 bg-[#0E1626] border border-[#1E293B] hover:border-amber-500/40 rounded-md px-3 py-2 transition-colors"
                    data-testid={`aic-starter-${i}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, idx) => (
              <Bubble key={idx} role={m.role} text={m.text} refused={m.refused} />
            ))
          )}
          {sending && (
            <div className="flex items-center gap-2 text-xs text-amber-300 font-mono uppercase tracking-wider" data-testid="aic-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Coach is thinking…
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-[#1E293B] p-3 flex items-end gap-2 bg-[#0B1220]"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder="Ask about a lawful investigative goal…"
            rows={2}
            className="flex-1 resize-none bg-[#0E1626] border border-[#1E293B] focus:border-amber-500/60 outline-none rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500"
            data-testid="aic-input"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-semibold text-sm rounded-md px-3.5 py-2.5 inline-flex items-center gap-1.5"
            data-testid="aic-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send
          </button>
        </form>
      </div>

      {/* Sessions sidebar */}
      <aside className={`${showHistory ? 'block' : 'hidden lg:block'} rounded-lg border border-[#1E293B] bg-[#0B1220]`} data-testid="aic-sessions">
        <div className="px-3 py-2.5 border-b border-[#1E293B] flex items-center justify-between">
          <h5 className="text-xs font-bold text-white uppercase tracking-wider font-mono">Past Sessions</h5>
          <button onClick={loadSessions} className="text-slate-400 hover:text-amber-300" title="Refresh">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="max-h-[560px] overflow-y-auto p-2 space-y-1.5">
          {sessions.length === 0 ? (
            <p className="text-[11px] text-slate-500 italic px-2 py-3">No previous sessions yet.</p>
          ) : (
            sessions.map(s => (
              <button
                key={s.session_id}
                onClick={() => loadHistory(s.session_id)}
                className={`w-full text-left rounded-md border px-2.5 py-2 transition-colors ${
                  sessionId === s.session_id
                    ? 'border-amber-500/50 bg-amber-500/5'
                    : 'border-slate-700 hover:border-slate-500 bg-[#0E1626]'
                }`}
                data-testid={`aic-session-${s.session_id}`}
              >
                <p className="text-[10px] font-mono text-amber-300 truncate">{s.session_id}</p>
                <p className="text-xs text-slate-200 mt-0.5 line-clamp-2">{s.preview || 'Untitled'}</p>
                <p className="text-[10px] text-slate-500 mt-1">{s.msg_count} msg{s.msg_count === 1 ? '' : 's'}</p>
              </button>
            ))
          )}
        </div>
      </aside>
    </div>
  );
};

export default AiCoachPanel;
